/**
 * Redux Slice for User Permissions Management
 * Stores user's permission codes loaded from backend
 * Handles caching with TTL and automatic refresh
 */

import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { normalizePermissionKeys } from "../utils/permissionKeyNormalization";

import * as permissionService from "@/features/userAccess/permissions/api/permissions.api";
import {
  isAfter,
  isSameOrBefore,
  isoStringMinutesFromNow,
  nowIsoString,
} from "@/lib/date";

export const PERMISSION_CACHE_TTL_MINUTES = 60; // 1 hour, matches backend

interface PermissionCacheEntry {
  permissionCodes: string[];
  loadedAt: string;
  expiresAt: string;
}

interface UserPermissionCache {
  [userTenantKey: string]: PermissionCacheEntry;
}

interface PermissionsState {
  // Current user's permissions for current tenant
  permissionCodes: string[];

  // Cache store for different user-tenant combinations
  cache: UserPermissionCache;

  // Loading state
  loading: boolean;
  refreshing: boolean;

  // Error handling
  error: string | null;
  lastLoadedAt: string | null;
  expiresAt: string | null;
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

const initialState: PermissionsState = {
  permissionCodes: [],
  cache: {},
  loading: false,
  refreshing: false,
  error: null,
  lastLoadedAt: null,
  expiresAt: null,
};

/**
 * Helper to create cache key for user-tenant combination
 */
function getCacheKey(userId: string, tenantId: string): string {
  return `${userId}:${tenantId}`;
}

/**
 * Helper to check if cache entry is still valid
 */
function isCacheValid(entry: PermissionCacheEntry): boolean {
  return isAfter(entry.expiresAt, nowIsoString());
}

/**
 * Helper to calculate expiration time
 */
function calculateExpiresAt(
  minutesFromNow: number = PERMISSION_CACHE_TTL_MINUTES,
): string {
  return isoStringMinutesFromNow(minutesFromNow);
}

/**
 * Async thunk to load user permissions from backend
 * Includes caching logic with TTL
 */
export const loadUserPermissionsAsync = createAsyncThunk(
  "permissions/loadUserPermissions",
  async (
    { userId, tenantId }: { userId: string; tenantId: string },
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as { permissions: PermissionsState };
      const cacheKey = getCacheKey(userId, tenantId);
      const cachedEntry = state.permissions.cache[cacheKey];

      // Return cached permissions if still valid
      if (cachedEntry && isCacheValid(cachedEntry)) {
        console.log("[Permissions] Cache hit for", cacheKey);
        return {
          permissionCodes: cachedEntry.permissionCodes,
          userId,
          tenantId,
          loadedAt: cachedEntry.loadedAt,
          fromCache: true,
        };
      }

      console.log(
        "[Permissions] Cache miss/expired for",
        cacheKey,
        "- fetching from backend",
      );

      // Fetch from backend
      const response = await permissionService.loadUserPermissions(userId, tenantId);

      if (!response.success) {
        return rejectWithValue(response.message || "Failed to load permissions");
      }

      const { permissionCodes, loadedAt } = response.data;

      return {
        permissionCodes,
        userId,
        tenantId,
        loadedAt,
        fromCache: false,
      };
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, "Failed to load permissions"));
    }
  },
);

/**
 * Async thunk to refresh (invalidate) user permission cache
 */
export const refreshPermissionCacheAsync = createAsyncThunk(
  "permissions/refreshCache",
  async (
    { userId, tenantId }: { userId: string; tenantId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await permissionService.refreshPermissionCache(userId, tenantId);

      if (!response.success) {
        return rejectWithValue(response.message || "Failed to refresh cache");
      }

      return { userId, tenantId };
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, "Failed to refresh cache"));
    }
  },
);

/**
 * Async thunk to assign tenant-level permission
 */
export const assignTenantPermissionAsync = createAsyncThunk(
  "permissions/assignTenantPermission",
  async (
    {
      tenantId,
      userId,
      permissionCode,
      isGranted,
      assignedByUserId,
    }: {
      tenantId: string;
      userId: string;
      permissionCode: string;
      isGranted: boolean;
      assignedByUserId?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await permissionService.assignTenantPermission(
        tenantId,
        userId,
        permissionCode,
        isGranted,
        assignedByUserId,
      );

      if (!response.success) {
        return rejectWithValue(response.message || "Failed to assign permission");
      }

      return {
        assignmentId: response.data,
        tenantId,
        userId,
        permissionCode,
        isGranted,
      };
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, "Failed to assign permission"));
    }
  },
);

/**
 * Async thunk to remove tenant-level permission
 */
export const removeTenantPermissionAsync = createAsyncThunk(
  "permissions/removeTenantPermission",
  async (
    {
      tenantId,
      userId,
      permissionCode,
    }: {
      tenantId: string;
      userId: string;
      permissionCode: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await permissionService.removeTenantPermission(
        tenantId,
        userId,
        permissionCode,
      );

      if (!response.success) {
        return rejectWithValue(response.message || "Failed to remove permission");
      }

      return { tenantId, userId, permissionCode };
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, "Failed to remove permission"));
    }
  },
);

const permissionsSlice = createSlice({
  name: "permissions",
  initialState,
  reducers: {
    /**
     * Manually set permissions (useful for testing or direct updates)
     */
    setPermissions(state, action: PayloadAction<string[]>) {
      state.permissionCodes = normalizePermissionKeys(action.payload);
      state.lastLoadedAt = nowIsoString();
      state.expiresAt = calculateExpiresAt();
      state.error = null;
    },

    /**
     * Clear all permissions
     */
    clearPermissions(state) {
      state.permissionCodes = [];
      state.cache = {};
      state.lastLoadedAt = null;
      state.expiresAt = null;
      state.error = null;
    },

    /**
     * Clear permissions cache for a specific user-tenant
     */
    clearCacheForUserTenant(
      state,
      action: PayloadAction<{ userId: string; tenantId: string }>,
    ) {
      const key = getCacheKey(action.payload.userId, action.payload.tenantId);
      delete state.cache[key];
    },

    /**
     * Clear entire cache
     */
    clearAllCache(state) {
      state.cache = {};
    },

    /**
     * Check if current permissions are expired
     */
    checkExpiration(state) {
      if (state.expiresAt && isSameOrBefore(state.expiresAt, nowIsoString())) {
        state.permissionCodes = [];
        state.expiresAt = null;
      }
    },
  },

  extraReducers: (builder) => {
    // Load user permissions
    builder
      .addCase(loadUserPermissionsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadUserPermissionsAsync.fulfilled, (state, action) => {
        const { permissionCodes, userId, tenantId, loadedAt, fromCache } = action.payload;
        const cacheKey = getCacheKey(userId, tenantId);

        state.permissionCodes = normalizePermissionKeys(permissionCodes);
        state.lastLoadedAt = loadedAt;
        state.expiresAt = calculateExpiresAt();
        state.loading = false;
        state.error = null;

        // Update cache
        state.cache[cacheKey] = {
          permissionCodes: state.permissionCodes,
          loadedAt,
          expiresAt: state.expiresAt,
        };

        if (!fromCache) {
          console.log(
            "[Permissions] Loaded",
            permissionCodes.length,
            "permissions for",
            cacheKey,
          );
        }
      })
      .addCase(loadUserPermissionsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        console.error("[Permissions] Load failed:", state.error);
      });

    // Refresh cache
    builder
      .addCase(refreshPermissionCacheAsync.pending, (state) => {
        state.refreshing = true;
        state.error = null;
      })
      .addCase(refreshPermissionCacheAsync.fulfilled, (state, action) => {
        const { userId, tenantId } = action.payload;
        const cacheKey = getCacheKey(userId, tenantId);

        // Invalidate cache entry
        delete state.cache[cacheKey];

        // Clear current permissions
        state.permissionCodes = [];
        state.expiresAt = null;
        state.refreshing = false;

        console.log("[Permissions] Cache invalidated for", cacheKey);
      })
      .addCase(refreshPermissionCacheAsync.rejected, (state, action) => {
        state.refreshing = false;
        state.error = action.payload as string;
        console.error("[Permissions] Refresh failed:", state.error);
      });

    // Assign tenant permission
    builder
      .addCase(assignTenantPermissionAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(assignTenantPermissionAsync.fulfilled, (state, action) => {
        const { tenantId, userId } = action.payload;
        const cacheKey = getCacheKey(userId, tenantId);

        // Invalidate cache
        delete state.cache[cacheKey];
        state.loading = false;

        console.log("[Permissions] Tenant permission assigned, cache invalidated");
      })
      .addCase(assignTenantPermissionAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Remove tenant permission
    builder
      .addCase(removeTenantPermissionAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeTenantPermissionAsync.fulfilled, (state, action) => {
        const { tenantId, userId } = action.payload;
        const cacheKey = getCacheKey(userId, tenantId);

        // Invalidate cache
        delete state.cache[cacheKey];
        state.loading = false;

        console.log("[Permissions] Tenant permission removed, cache invalidated");
      })
      .addCase(removeTenantPermissionAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setPermissions,
  clearPermissions,
  clearCacheForUserTenant,
  clearAllCache,
  checkExpiration,
} = permissionsSlice.actions;

export default permissionsSlice.reducer;
